/*
* Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
  distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
  prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
  content, please contact the author for proper authorization and agreement.
  */
import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ContactsAction } from 'store/contacts/ContactsAction'
import { useAppDispatch } from 'helpers/store'

import UiCard, { UiCardHeader, UiCardTitle } from 'components/Ui/UiCard'
import UiTanstackTable from 'components/Ui/UiTanstackTable'
import UiTabSwitcher from 'components/Ui/UiTabSwitcher'
import UiLoader from 'components/Ui/UiLoader'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiInitialsAvatar from 'components/Ui/UiInitialsAvatar'
import CompanyContactForm from 'components/Companies/forms/CompanyContactForm'
import { UiTabVariant } from 'types/enums/UiTabVariant'
import { useContactCompanyDetail } from 'utils/hooks/useContactCompanyDetail'
import { ContactDetailTab } from 'types/enums/specific/ContactDetailTab'
import { getCompanyContactDisplayName } from 'utils/companiesUtils'
import { isGenericCompanyContact } from 'utils/contacts/genericContactUtils'

interface ContactCompanyDetailProps {
    contactId?: number
    embedded?: boolean
}

const ContactCompanyDetail: React.FC<ContactCompanyDetailProps> = (props) => {
    const params = useParams<{ contactId: string }>()
    const contactIdStr = props.contactId ? props.contactId.toString() : params.contactId
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const {
        contact,
        activeTab,
        setActiveTab,
        tabItems,
        tableInit,
        handleAssignClick,
        handleRowClick,
        handleCreateLead,
    } = useContactCompanyDetail({ contactIdStr })

    if (!contact) {
        return <UiLoader variant="page" />
    }

    return (
        <div className={clsx('ContactCompanyDetail h-full flex flex-col', !props.embedded && 'p-6')}>
            <div className={clsx('ContactCompanyDetailContent', 'grow min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch')}>
                <div className={clsx('ContactInfoColumn', 'lg:col-span-4 sticky top-6')}>
                    <UiCard padding="none" className={clsx('ContactInfoCard', 'h-full flex flex-col')}>
                        <div className={clsx('ProfileHeader', 'relative p-6 flex flex-col items-center text-center bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-800')}>
                            <UiInitialsAvatar
                                firstName={contact.first_name}
                                lastName={contact.last_name}
                                size="xl"
                                className="mb-4"
                            />
                            <h2 className={clsx('Name', 'text-2xl font-bold text-gray-900 dark:text-white')}>
                                {getCompanyContactDisplayName(contact) || `${contact.first_name} ${contact.last_name}`}
                            </h2>
                            {isGenericCompanyContact(contact) ? (
                                <span className="mt-1 inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-900/40 dark:text-blue-200">
                                    {t('Generic contact')}
                                </span>
                            ) : null}
                            <p className={clsx('Position', 'text-cta-light font-semibold mt-1')}>
                                {contact.position || t('No position')}
                            </p>
                            <div className="mt-4">
                                <UiButton
                                    style={UiButtonStyle.Primary}
                                    size={UiButtonSize.Small}
                                    onClick={handleCreateLead}
                                >
                                    {t('Create Lead')}
                                </UiButton>
                            </div>
                        </div>
                        <div className="grow overflow-auto px-4 pb-4">
                            <CompanyContactForm
                                contact={contact}
                                inline={true}
                                onChange={(updated) => {
                                    dispatch(ContactsAction.setSelectedContact(updated))
                                    dispatch(ContactsAction.fetchContactById(updated.id))
                                }}
                            />
                        </div>
                    </UiCard>
                </div>

                <div className={clsx('AssignedInfoColumn', 'lg:col-span-8 flex flex-col gap-6')}>
                    <UiCard padding="none" className={clsx('AssignedItemsCard', 'flex flex-col h-full')}>
                        <UiCardHeader className={clsx('p-5 pr-16 mb-0 border-b border-gray-100 dark:border-gray-800')}>
                            <UiCardTitle>{t('Assigned Items')}</UiCardTitle>
                            {activeTab === ContactDetailTab.Accounts && (
                                <UiButton
                                    onClick={handleAssignClick}
                                    style={UiButtonStyle.Primary}
                                    size={UiButtonSize.Small}
                                    className="h-10"
                                >
                                    {t('Assign to company')}
                                </UiButton>
                            )}
                        </UiCardHeader>
                        <div className="px-5 pt-3 border-b border-gray-100 dark:border-gray-800">
                            <UiTabSwitcher
                                items={tabItems}
                                selected={activeTab}
                                onClick={(tabId) => setActiveTab(tabId as ContactDetailTab)}
                                variant={UiTabVariant.Underline}
                            />
                        </div>
                        <div className={clsx('overflow-hidden grow min-h-0 flex flex-col')}>
                            <UiTanstackTable
                                tableInit={tableInit}
                                className="grow min-h-0"
                                hidePagination
                                onRowClick={(row) => handleRowClick(row.original)}
                            />
                        </div>
                    </UiCard>
                </div>
            </div>
        </div>
    )
}

export default ContactCompanyDetail
